import { ImageExtension, ImageOptions } from "@remirror/extension-image";
import { PasteRule } from "prosemirror-paste-rules";

interface CustomImageOptions extends ImageOptions {
  canvasId: string;
  onImageUploadError: (message?: string) => void;
  onImageUploadStart?: () => void;
  enableResizing: boolean;
}

interface ImageUploadResponse {
  id: string;
}

export class CustomImageExtension extends ImageExtension {
  private canvasId: string;
  private onImageUploadError: (message?: string) => void;
  private onImageUploadStart: () => void;
  private readonly MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  private readonly MAX_IMAGE_SIZE_MB = 10; // For error messages

  constructor(options: Partial<CustomImageOptions> = {}) {
    super({
      ...options,
      enableResizing: options.enableResizing,
    });
    this.canvasId = options.canvasId || "";
    this.onImageUploadError = options.onImageUploadError || (() => {});
    this.onImageUploadStart = options.onImageUploadStart || (() => {});
  }

  /**
   * Verifies that an image URL is valid and accessible
   * Returns a promise that resolves to true if the image loads successfully
   * Times out after 5 seconds
   */
  private async verifyImageUrl(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => resolve(false), 5000);
      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      img.src = url;
    });
  }

  private async handleImageUpload(files: File[]) {
    if (!files.length) {
      this.onImageUploadError("No image provided");
      return;
    }

    const file = files[0];
    if (!file.size || file.size === 0) {
      this.onImageUploadError("Invalid or empty image file");
      return;
    }

    if (file.size > this.MAX_IMAGE_SIZE) {
      this.onImageUploadError(
        `This ${(file.size / (1024 * 1024)).toFixed(2)} MB image exceeds the maximum size of ${this.MAX_IMAGE_SIZE_MB} MB.`,
      );
      return;
    }

    this.onImageUploadStart();

    const tempUrl = URL.createObjectURL(file);
    this.store.commands.insertImage({
      src: tempUrl,
      // Allows browser to decode image asynchronously without blocking the main thread
      decoding: "async",
      // Signals high priority to browser for faster loading of actively inserted images
      fetchpriority: "high",
    });

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(
        `/api/canvas/v1/canvases/${this.canvasId}/images`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Upload image error:", errorData);
        this.removeTempImage(tempUrl);
        this.onImageUploadError(errorData.error);
        return;
      }

      const { id } = (await response.json()) as ImageUploadResponse;
      const imageUrl = `/api/canvas/v1/canvases/${this.canvasId}/images/${id}`;

      /* Verify permissions before replacing blob URL to prevent
       * image flicker during AWS authorization check
       */
      const isImageValid = await this.verifyImageUrl(imageUrl);
      if (!isImageValid) {
        throw new Error("Uploaded image is not accessible");
      }

      // Replace temp image with final URL
      this.replaceTempImage(tempUrl, imageUrl);
    } catch (error) {
      console.error("Error uploading image:", error);
      // Remove image from editor content
      this.removeTempImage(tempUrl);
      this.onImageUploadError();
    } finally {
      // Clean up blob URL from browser memory
      URL.revokeObjectURL(tempUrl);
    }
  }

  /* Removes the image node from the ProseMirror editor's content
     Like deleting an <img> tag from HTML
  */
  private removeTempImage(tempUrl: string) {
    const { state } = this.store.view;
    const tr = state.tr;

    state.doc.descendants((node, pos) => {
      if (
        node.type === state.schema.nodes.image &&
        node.attrs.src === tempUrl
      ) {
        tr.delete(pos, pos + 1);
        return false;
      }
      return true;
    });

    if (tr.docChanged) {
      this.store.view.dispatch(tr);
    }
  }

  private replaceTempImage(tempUrl: string, finalUrl: string) {
    const { state } = this.store.view;
    const tr = state.tr;

    // Collect all image replacements in a single transaction
    state.doc.descendants((node, pos) => {
      if (
        node.type === state.schema.nodes.image &&
        node.attrs.src === tempUrl
      ) {
        tr.setNodeMarkup(pos, state.schema.nodes.image, {
          ...node.attrs,
          src: finalUrl,
        });
      }
      return true;
    });

    if (tr.docChanged) {
      // Apply all updates at once
      this.store.view.dispatch(tr);
      // Trigger only one save after all replacements
      this.store.view.dom.dispatchEvent(new Event("blur"));
    }
  }

  createPasteRules(): PasteRule[] {
    return [
      {
        type: "file",
        regexp: /image/i,
        fileHandler: (props) => {
          if (!this.canvasId) {
            return true;
          }

          this.handleImageUpload(props.files);
          return true;
        },
      },
    ];
  }
}
